import { gsap } from "gsap";
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import { isTouch } from '../_app/cuchillo/core/Basics';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Interaction } from '../_app/cuchillo/core/Interaction';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { Sizes } from '../_app/cuchillo/core/Sizes';
import { Ease } from '../_app/cuchillo/utils/Ease';
import { Maths } from '../_app/cuchillo/utils/Maths';
import ScrollItem__BlockDefault from './ScrollItem__BlockDefault';

const SETTINGS = {
    ease: .15,      // Inercia con la que la estampa persigue al puntero (0-1 por frame)
    stamps: 5,      // Frames seguidos en los que se estampa cada imagen
    gap: 1,         // Frames sin pintar antes de pasar a la siguiente imagen
    minMove: 1,     // Px mínimos de avance para estampar: en reposo no se pinta nada

    layers: 4,      // Canvas apilados: uno recibe estampas, el resto se va borrando
    fade: 4,        // Segundos que tarda en borrarse una capa desde que deja de recibir estampas

    bursts: 3,                              // Touch: ráfagas en marcha a la vez. Arranca una cada burstTime / bursts
    burstTime: 1.6,                         // Touch: segundos que dura una ráfaga
    burstEase: Ease.EASE_CUCHILLO_IN_OUT,   // Touch: aceleración a lo largo de la ráfaga
};

// Tiempo que una capa recibe estampas: al volver a tocarle el turno ya ha terminado de borrarse
const LAYER_TIME = SETTINGS.fade / (SETTINGS.layers - 1);

class ScrollItem__BlockGalleryVortex extends ScrollItem__BlockDefault {
    _call;
    _launcher;
    _brushes = [];      // Trazos: uno que sigue al puntero o, en touch, uno por ráfaga simultánea
    _next = 0;          // Trazo al que le toca la siguiente ráfaga
    _layers = [];       // Contextos 2D, del más antiguo al activo (el último, encima del resto)
    _ctx;               // Capa activa
    _stampedAt = null;  // Momento de la primera estampa de la capa activa (null = limpia)
    _sources = [];
    _images = [];       // Originales, en el orden de la galería
    _sprites = [];      // Cada imagen rasterizada una vez al tamaño final: estampar es copiar píxeles
    _index = 0;         // Última imagen repartida a un trazo
    _ratio = 1;
    _size = 0;          // Lado largo de la estampa en px de canvas
    _left = 0;
    _isInside = false;  // Puntero dentro del bloque en el frame anterior

    //==================================================================================================================
    //          CONSTRUCTOR
    //==================================================================================================================

    constructor(__link, __index, __scroller) {
        super(__link, __index, __scroller);

        this._sources = [...GetBy.selector('.__holder li', __link)].map(item => item.textContent.trim());

        for (let i = 0; i < SETTINGS.layers; i++) {
            const canvas = document.createElement('canvas');
            canvas.className = 'block-gallery-vortex__canvas';
            __link.appendChild(canvas);

            this._ctx = canvas.getContext('2d');
            this._layers.push(this._ctx);
        }

        // Posiciones en px CSS relativos al bloque. frame = punto del ciclo stamps + gap
        for (let i = 0; i < (isTouch ? SETTINGS.bursts : 1); i++) {
            this._brushes.push({ x: 0, y: 0, lastX: 0, lastY: 0, index: i, frame: 0, t: 0, tween: null });
        }
        this._index = this._brushes.length - 1;

        // No es loop(): Scroll_Item.update() ya llama a loop() en cada evento de scroll y duplicaría estampas
        this._call = () => this.tick();
        this.onShow = () => {
            if (this.firstShow) this._load();
            if (isTouch) this._launch();
            gsap.ticker.add(this._call);
        };

        this.onHide = () => {
            this._killBursts();
            gsap.ticker.remove(this._call);
        };
    }

    //==================================================================================================================
    //          IMAGES
    //==================================================================================================================

    // Carga en serie: respeta el orden de la galería y solo hay una imagen decodificándose a la vez
    _load(__index = 0) {
        if (!this.item || __index >= this._sources.length) return;

        const image = new Image();
        image.src = this._sources[__index];
        image.decode()
            .then(() => {
                if (!this.item) return;
                this._images.push(image);
                this._sprites.push(this._rasterize(image));
            })
            .catch(() => { })
            .finally(() => this._load(__index + 1));
    }

    _rasterize(__image) {
        const scale = this._size / Math.max(__image.naturalWidth, __image.naturalHeight);
        const sprite = document.createElement('canvas');
        sprite.width = Math.round(__image.naturalWidth * scale);
        sprite.height = Math.round(__image.naturalHeight * scale);

        const ctx = sprite.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(__image, 0, 0, sprite.width, sprite.height);

        return sprite;
    }

    //==================================================================================================================
    //          LAYERS
    //==================================================================================================================

    // La capa activa empieza a borrarse y la más antigua, ya transparente, vuelve limpia encima del resto.
    // El borrado es opacidad de una capa ya compuesta: no se repinta ni un píxel del canvas
    _rotate() {
        gsap.to(this._ctx.canvas, { opacity: 0, duration: SETTINGS.fade, ease: 'none' });

        this._ctx = this._layers.shift();
        this._layers.push(this._ctx);
        this._stampedAt = null;

        const canvas = this._ctx.canvas;
        gsap.killTweensOf(canvas);
        gsap.set(canvas, { opacity: 1 });
        this._ctx.clearRect(0, 0, canvas.width, canvas.height);
        this.item.appendChild(canvas);
    }

    //==================================================================================================================
    //          BURST
    //==================================================================================================================

    // Sin puntero los trazos se mueven solos: cada tercio de ráfaga (burstTime / bursts) arranca una nueva en el
    // trazo siguiente, que acaba de terminar la suya. Siempre hay tres en marcha, desfasadas un tercio
    _launch() {
        this._burst(this._brushes[this._next]);
        this._next = (this._next + 1) % this._brushes.length;
        this._launcher = gsap.delayedCall(SETTINGS.burstTime / SETTINGS.bursts, () => this._launch());
    }

    // Ráfaga: recorre una Bézier cúbica entre cuatro puntos al azar del bloque (sale del primero, acaba en el
    // último y los dos centrales la curvan)
    _burst(__brush) {
        const random = gsap.utils.random;
        const p = Array.from({ length: 4 }, () => ({ x: random(0, this.width), y: random(0, this.height) }));

        if (__brush.tween) __brush.tween.kill();
        __brush.tween = gsap.fromTo(__brush, { t: 0 }, {
            t: 1,
            duration: SETTINGS.burstTime,
            ease: SETTINGS.burstEase,
            onUpdate: () => {
                const t = __brush.t;
                const u = 1 - t;
                const a = u * u * u;
                const b = 3 * u * u * t;
                const c = 3 * u * t * t;
                const d = t * t * t;

                __brush.x = a * p[0].x + b * p[1].x + c * p[2].x + d * p[3].x;
                __brush.y = a * p[0].y + b * p[1].y + c * p[2].y + d * p[3].y;
            }
        });
    }

    _killBursts() {
        if (this._launcher) this._launcher.kill();
        this._brushes.forEach(brush => brush.tween && brush.tween.kill());
    }

    //==================================================================================================================
    //          LOOP
    //==================================================================================================================

    tick() {
        if (!this._sprites.length) return;

        if (this._stampedAt !== null && gsap.ticker.time - this._stampedAt > LAYER_TIME) this._rotate();

        if (!isTouch) {
            const mouse = Interaction.positions.mouse;
            const brush = this._brushes[0];

            // Puntero en coordenadas del bloque. -this.y es su top en el viewport: sin lecturas de layout
            const x = mouse.x - this._left;
            const y = mouse.y + this.y;
            const isInside = x > 0 && x < this.width && y > 0 && y < this.height;

            // Solo se pinta con el puntero dentro. Al entrar, el trazo nace en el puntero: sin rastro desde fuera
            if (isInside && this._isInside) {
                brush.x = Maths.lerp(brush.x, x, SETTINGS.ease);
                brush.y = Maths.lerp(brush.y, y, SETTINGS.ease);
            } else if (isInside) {
                brush.x = brush.lastX = x;
                brush.y = brush.lastY = y;
            }

            this._isInside = isInside;
        }

        for (let i = 0; i < this._brushes.length; i++) this._stamp(this._brushes[i]);
    }

    _stamp(__brush) {
        if (Math.abs(__brush.x - __brush.lastX) + Math.abs(__brush.y - __brush.lastY) < SETTINGS.minMove) return;

        __brush.lastX = __brush.x;
        __brush.lastY = __brush.y;

        if (__brush.frame < SETTINGS.stamps) {
            const sprite = this._sprites[__brush.index % this._sprites.length];

            // Coordenadas enteras = copia sin remuestreo
            this._ctx.drawImage(
                sprite,
                Math.round(__brush.x * this._ratio - sprite.width * .5),
                Math.round(__brush.y * this._ratio - sprite.height * .5)
            );

            if (this._stampedAt === null) this._stampedAt = gsap.ticker.time;
        }

        // Cada trazo coge la siguiente imagen de la galería: dos trazos no repiten imagen
        if (++__brush.frame === SETTINGS.stamps + SETTINGS.gap) {
            __brush.frame = 0;
            __brush.index = ++this._index;
        }
    }

    //==================================================================================================================
    //          RESIZE
    //==================================================================================================================

    resize() {
        super.resize();

        this._ratio = Sizes.RATIO_CANVAS;
        this._left = this.item.getBoundingClientRect().left;

        const width = Math.round(this.width * this._ratio);
        const height = Math.round(this.height * this._ratio);
        const size = Math.round(Metrics.parseSize(getComputedStyle(this.item).getPropertyValue('--card-size')) * this._ratio);

        // Scroll.resize() salta con cualquier cambio de altura de la página y redimensionar un canvas lo borra:
        // solo se toca si su tamaño ha cambiado de verdad
        if (width !== this._ctx.canvas.width || height !== this._ctx.canvas.height) {
            this._layers.forEach(ctx => {
                ctx.canvas.width = width;
                ctx.canvas.height = height;
            });
            this._stampedAt = null;
        }

        if (size !== this._size) {
            this._size = size;
            this._sprites = this._images.map(image => this._rasterize(image));
        }
    }

    dispose() {
        gsap.ticker.remove(this._call);
        this._killBursts();

        this._layers.forEach(ctx => {
            gsap.killTweensOf(ctx.canvas);
            ctx.canvas.remove();
        });
        this._images = [];
        this._sprites = [];

        super.dispose();
    }
}

Scroll._registerClass('BlockGalleryVortex', ScrollItem__BlockGalleryVortex);

export default ScrollItem__BlockGalleryVortex;
